import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { BtnSecondary, Pagination } from "@/pages/Senders";
import { DownloadSimple, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Reports() {
  const [logs, setLogs] = useState([]);
  const [f, setF] = useState({ campaign_id: "", status: "", sender_email: "", recipient_email: "" });
  const [campaigns, setCampaigns] = useState([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [totalCount, setTotalCount] = useState(0);
  const [clearing, setClearing] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const load = async () => {
    const params = {};
    Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
    params.limit = pageSize;
    params.skip = page * pageSize;
    const r = await api.get("/logs", { params });
    setLogs(r.data.logs);
    setTotalCount(r.data.total || 0);
  };
  useEffect(() => {
    api.get("/campaigns").then((r) => setCampaigns(r.data.campaigns));
    setPage(0);
    // eslint-disable-next-line
  }, [f.campaign_id, f.status]);
  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [f.campaign_id, f.status, page, pageSize]);

  const download = async (format) => {
    const token = localStorage.getItem("ce_token");
    const url = `${API}/logs/export?format=${format}${f.campaign_id ? `&campaign_id=${f.campaign_id}` : ""}`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `logs.${format}`;
    a.click();
  };

  const clearLogs = async () => {
    setClearing(true);
    try {
      const params = {};
      Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
      const r = await api.delete("/logs", { params });
      const scoped = f.campaign_id || f.status || f.sender_email || f.recipient_email;
      toast.success(`Cleared ${r.data.deleted} log row${r.data.deleted === 1 ? "" : "s"}${scoped ? " (filtered)" : ""}`);
      setPage(0);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to clear logs");
    } finally {
      setClearing(false);
      setConfirmOpen(false);
    }
  };

  const activeCampaignName = f.campaign_id
    ? (campaigns.find((c) => c.id === f.campaign_id)?.name || f.campaign_id)
    : null;
  const filterActive = !!(f.campaign_id || f.status || f.sender_email || f.recipient_email);

  return (
    <div className="p-8">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="micro mb-1">Operations</div>
          <h1 className="font-heading text-4xl font-bold tracking-tighter">Reports & Logs</h1>
        </div>
        <div className="flex gap-2">
          <BtnSecondary onClick={() => download("csv")} testid="export-csv"><DownloadSimple size={14} weight="bold" /> CSV</BtnSecondary>
          <BtnSecondary onClick={() => download("xlsx")} testid="export-xlsx"><DownloadSimple size={14} weight="bold" /> XLSX</BtnSecondary>
          <button
            data-testid="reports-clear-btn"
            onClick={() => setConfirmOpen(true)}
            disabled={clearing || totalCount === 0}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-mono uppercase tracking-wider border border-[#FF3B30] text-[#FF3B30] hover:bg-[#1a0a0a] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            title={filterActive ? `Clear ${totalCount} filtered log${totalCount === 1 ? "" : "s"}` : `Clear all ${totalCount} logs`}
          >
            <Trash size={14} weight="bold" /> {clearing ? "Clearing…" : "Clear Logs"}
          </button>
        </div>
      </div>

      {confirmOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="reports-clear-confirm">
          <div className="border border-[#FF3B30] bg-[#0A0A0A] p-6 max-w-md w-full">
            <div className="micro text-[#FF3B30] mb-2">Confirm Destructive Action</div>
            <h2 className="font-heading text-xl font-bold mb-3 text-white">Clear log entries?</h2>
            <p className="text-sm text-[#A1A1AA] mb-4 leading-relaxed">
              {filterActive ? (
                <>You are about to delete <span className="font-mono text-white">{totalCount}</span> log
                  row{totalCount === 1 ? "" : "s"} matching your current filters
                  {activeCampaignName ? <> for campaign <span className="font-mono text-[#E1FF00]">{activeCampaignName}</span></> : null}.
                  This does <b>not</b> affect campaign queues or delivery — only historical log rows.</>
              ) : (
                <>You are about to delete <b>ALL {totalCount} log rows</b> across every campaign.
                  This cannot be undone. Sending queues and campaign state remain intact.</>
              )}
            </p>
            <div className="flex justify-end gap-2">
              <button
                data-testid="reports-clear-cancel"
                onClick={() => setConfirmOpen(false)}
                className="px-4 py-2 text-xs font-mono uppercase tracking-wider border border-[#2A2A2A] text-[#A1A1AA] hover:text-white hover:border-[#3A3A3A]"
              >
                Cancel
              </button>
              <button
                data-testid="reports-clear-confirm-btn"
                onClick={clearLogs}
                disabled={clearing}
                className="px-4 py-2 text-xs font-mono uppercase tracking-wider bg-[#FF3B30] text-white hover:bg-[#e0342b] disabled:opacity-50"
              >
                {clearing ? "Clearing…" : "Yes, clear"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex gap-2 mb-4 flex-wrap">
        <select value={f.campaign_id} onChange={(e) => setF({ ...f, campaign_id: e.target.value })}
          data-testid="reports-filter-campaign"
          className="bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-2 text-xs text-white">
          <option value="">any campaign</option>
          {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}
          data-testid="reports-filter-status"
          className="bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-2 text-xs text-white">
          <option value="">any status</option>
          <option value="provider_accepted">provider accepted</option>
          <option value="proxy_failure">proxy failure</option>
          <option value="connection_error">connection error</option>
          <option value="failed">failed</option>
          <option value="authentication_error">auth error</option>
        </select>
        <input placeholder="sender email..." value={f.sender_email} onChange={(e) => setF({ ...f, sender_email: e.target.value })}
          onKeyDown={(e) => e.key === "Enter" && load()}
          data-testid="reports-filter-sender"
          className="bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-2 text-xs text-white" />
        <input placeholder="recipient email..." value={f.recipient_email} onChange={(e) => setF({ ...f, recipient_email: e.target.value })}
          onKeyDown={(e) => e.key === "Enter" && load()}
          data-testid="reports-filter-recipient"
          className="bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-2 text-xs text-white" />
        <BtnSecondary onClick={load} testid="reports-apply">Apply</BtnSecondary>
      </div>

      <div className="border border-[#2A2A2A] bg-[#0A0A0A] overflow-x-auto">
        <table className="dtable w-full" data-testid="reports-table">
          <thead>
            <tr>
              <th>Time</th>
              <th>Batch</th>
              <th>Cycle</th>
              <th>Sender</th>
              <th>Recipient</th>
              <th>Proxy IP</th>
              <th className="num">RT ms</th>
              <th>Status</th>
              <th>CTA link</th>
              <th>Provider response</th>
              <th>Error</th>
            </tr>
          </thead>
          <tbody>
            {logs.length === 0 && (
              <tr><td colSpan={11} className="text-center text-[#71717A] py-8 font-mono">[ no logs ]</td></tr>
            )}
            {logs.map((l) => (
              <tr key={l.id}>
                <td className="font-mono text-[10px] text-[#71717A]">{l.created_at?.slice(0, 19)}</td>
                <td className="data-num">B{l.batch_number || 0}</td>
                <td className="data-num">{l.sender_cycle_number || 0}</td>
                <td className="font-mono text-xs">{l.sender_email}</td>
                <td className="font-mono text-xs">{l.recipient_email}</td>
                <td className="font-mono text-xs">{l.proxy_public_ip}</td>
                <td className="num data-num text-[#71717A]">{l.proxy_response_time_ms || "—"}</td>
                <td><StatusBadge status={l.status} /></td>
                <td className="font-mono text-[11px] text-[#00E5FF] truncate max-w-[140px]">{l.cta_link}</td>
                <td className="font-mono text-[11px] text-[#71717A] truncate max-w-xs">{l.provider_response}</td>
                <td className="font-mono text-[11px] text-[#FF3B30] truncate max-w-xs">{l.error_message || l.proxy_error_message}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalCount > 0 && (
        <Pagination
          page={page}
          pageSize={pageSize}
          total={totalCount}
          onPageChange={setPage}
          onPageSizeChange={(s) => { setPageSize(s); setPage(0); }}
          testidPrefix="reports-pagination"
        />
      )}

    </div>
  );
}
